import type { Permission } from './permissions.js';
import { z } from 'zod';
import type { IsoDate } from './common.js';
import type { UserSummary } from './users.js';

export const channelKinds = ['channel', 'dm'] as const;
export type ChannelKind = (typeof channelKinds)[number];

/** `voice`: a Discord-style voice/video room instead of a text conversation. */
export const channelModes = ['text', 'voice'] as const;
export type ChannelMode = (typeof channelModes)[number];

export const channelNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, 'Obligatorio')
  .max(40)
  .regex(/^[a-z0-9][a-z0-9-_]*$/, 'Usa minúsculas, números, - o _');

export const createChannelSchema = z.object({
  name: channelNameSchema,
  description: z.string().trim().max(250).optional(),
  isPrivate: z.boolean().default(false),
  mode: z.enum(channelModes).default('text'),
  /** Extra members for private channels (the creator is always added). */
  memberIds: z.array(z.uuid()).max(50).default([]),
});
export type CreateChannelInput = z.input<typeof createChannelSchema>;

export const updateChannelSchema = z.object({
  name: channelNameSchema.optional(),
  description: z.string().trim().max(250).nullable().optional(),
});
export type UpdateChannelInput = z.infer<typeof updateChannelSchema>;

export const openDmSchema = z.object({ userId: z.uuid() });
export type OpenDmInput = z.infer<typeof openDmSchema>;

/** How much a conversation notifies you (`default`: your global preference; DMs: everything). */
export const notifyLevels = ['default', 'all', 'mentions', 'none'] as const;
export type NotifyLevel = (typeof notifyLevels)[number];

export const channelNotificationsSchema = z.object({
  level: z.enum(notifyLevels).optional(),
  /** Null unmutes. A far-future date mutes "until I turn it back on". */
  mutedUntil: z.iso.datetime().nullable().optional(),
});
export type ChannelNotificationsInput = z.infer<typeof channelNotificationsSchema>;

export interface Channel {
  id: string;
  teamId: string;
  kind: ChannelKind;
  /** Always `text` for DMs. */
  mode: ChannelMode;
  /** For DMs this is empty; display the other participant instead. */
  name: string;
  description: string | null;
  isPrivate: boolean;
  createdAt: IsoDate;
  /** Other participant of a DM (null for regular channels). */
  dmPeer: UserSummary | null;
  unreadCount: number;
  /** Unread messages that mention you (or any unread message in a DM). */
  mentionCount: number;
  notifyLevel: NotifyLevel;
  mutedUntil: IsoDate | null;
  lastMessageAt: IsoDate | null;
  /** What the requesting user can do in this channel (see permissions.ts). */
  permissions: Permission[];
}
